import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { ENUMS, PRICE_CONFIGURATIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

/**
 * @author @muhammedburakak
 */

const SuccessInstructionsForm = NiceModal.create(({ ID, mode, modalName }) => {
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();
	const dispatch = useDispatch();
	const { closeModal } = useModalRouter();
	const { showSuccess, showError } = useAlert();

	const defaultColSpan = {
		md: 6
	};
	const viewMode = mode === 'view';

	const [isLoading, setIsLoading] = useState(false);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					// product Details
					shippingContractId: yup.number().nullable().required(),
					instructions: yup.array().of(
						yup.object().shape({
							instructionType: yup.string().nullable().required()
						})
					)
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			shippingContractId: null,
			instructions: [
				{
					instructionType: null
				}
			]
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		reset,
		control,
		formState: { isDirty, isSubmitting, isSubmitSuccessful }
	} = formMethods;

	const instructionsWatcher = useWatch({ control, name: 'instructions' });

	const { fields, append, remove, move } = useFieldArray({
		name: 'instructions',
		control
	});

	const controlledFields = fields.map((field, index) => {
		if (!instructionsWatcher) return field;
		return {
			...field,
			...instructionsWatcher[index]
		};
	});

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const fillForm = useCallback(() => {
		if (ID) {
			setIsLoading(true);
			axiosPrivate
				.post(PRICE_CONFIGURATIONS.GET_SUCCESS_INSTRUCTION, {
					shippingContractId: ID
				})
				.then((response) => {
					const { data, status } = response;

					if (status === 200) {
						// reset form fields

						reset({
							shippingContractId: data.shippingContractId,
							instructions: data.successInstructionList.map((item) => ({
								instructionType: item.instructionType
							}))
						});
					} else AppLogger.error(response);
				})
				.catch((error) => {
					showError({
						message: error?.response?.data?.code || error?.response?.data?.message
					});
					AppLogger.error(error);
				})
				.finally(() => setIsLoading(false));
		}
	}, [ID]);

	useEffect(() => {
		if (ID) fillForm();
	}, [ID]);

	const onSubmit = (data) => {
		setIsLoading(true);

		axiosPrivate
			.post(PRICE_CONFIGURATIONS.ADD_SUCCESS_INSTRUCTION, {
				shippingContractId: data.shippingContractId,
				successInstructionList: data.instructions.map((item, index) => ({
					priority: index,
					instructionType: item.instructionType
				}))
			})
			.then((response) => {
				const { status } = response;

				if (status === 200) {
					showSuccess();
					dispatch(updateDynamicListData(DYNAMICLISTS.SUCCESS_INSTRUCTIONS));
					handleCloseModal();
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			})
			.finally(() => setIsLoading(false));
	};

	const fetchAutocompleteData = useCallback(async (props) => {
		const url =
			mode === 'edit'
				? PRICE_CONFIGURATIONS.GET_SHIPPING_CONTRACT_AUTOCOMPLETE_LIST
				: PRICE_CONFIGURATIONS.GET_SHIPPING_CONTRACT_AUTOCOMPLETE_FOR_INSTRUCTION;

		const data = await axiosPrivate.post(url).catch((error) => {
			showError();
			AppLogger.error(error);
		});

		return data.data.filter((item) =>
			props?.keyword
				? item?.name?.toLowerCase().includes(props?.keyword.toLowerCase()) ||
					item?.deliveryCompanySettingsId?.toString().toLowerCase().includes(props?.keyword.toLowerCase())
				: props?.IDs
					? props?.IDs.includes(item.ID)
					: true
		);
	}, []);

	const shippingContractAutocompleteColumns = useMemo(
		() => [
			{ accessor: 'deliveryCompanySettingsId', width: 50 },
			{ accessor: 'companyName', width: 100 },
			{ accessor: 'name', width: 200 }
		],
		[]
	);

	const columns = useMemo(
		() => [
			{
				header: 'instructionType',
				accessorKey: 'instructionType',
				required: true,
				enableSorting: false,
				size: 100,
				renderCell: ({ row }) => (
					<EnumSelect
						enumName={ENUMS.INSTRUCTION_TYPE}
						name={`instructions[${row.index}].instructionType`}
						disabled={viewMode}
						value={watch(`instructions[${row.index}].instructionType`)}
						setValue={(value) => {
							setValue(`instructions[${row.index}].instructionType`, value, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
					/>
				),
				showOnMobile: true
			},

			...(mode === 'edit' || mode === 'add'
				? [
						{
							id: 'deleteRow',
							size: 20,
							maxSize: 20,
							showOnMobile: true
						}
					]
				: [])
		],
		[]
	);

	return (
		<Modal fullwidth maxWidth="medium" name={modalName} className="modal-successInstructions">
			<ModalTitle>
				<Typography variant="p1">successInstructionsForm</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods} borderless viewPrintButton={viewMode}>
					<FormItem colSpan={defaultColSpan} label="shippingContract" isLoading={isLoading} required>
						<AutoComplete
							fetchFunction={fetchAutocompleteData}
							columns={shippingContractAutocompleteColumns}
							value={watch('shippingContractId')}
							setValue={(value) => {
								setValue('shippingContractId', value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
								setValue('currency', value?.currency);
							}}
							name={`shippingContractId`}
							selectionLabelAccessor={['deliveryCompanySettingsId', 'name']}
						/>
					</FormItem>

					<Table
						columns={columns}
						data={controlledFields}
						loading={isLoading}
						showAddNewRowButton={mode !== 'view'}
						onRowDelete={(item) => remove(item.index)}
						onRowDrag={viewMode ? undefined : move}
						onRowAdd={() => {
							append({ instructionType: null });
						}}
					/>
				</Form>
			</ModalContent>

			{!viewMode && (
				<ModalActions>
					<Button label="cancel" variant="outline" onClick={handleCloseModal} />

					<Button
						loading={isLoading}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						label={mode === 'edit' ? 'save' : 'button.add'}
					/>
				</ModalActions>
			)}
		</Modal>
	);
});

export default SuccessInstructionsForm;
